import { createContext, useContext, useState, useEffect, useLayoutEffect, ReactNode, useCallback, useRef } from 'react';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { Capacitor } from '@capacitor/core';
import { authFetch, buildApiUrl, setAccessTokenProvider } from '@/config/api';
import { authEvents, AUTH_EVENTS } from '@/lib/authEvents';
import { getNativeTokens, isNativeTokenValid, clearNativeTokens, removeNativeAuthLoadingOverlay, isNativeAccessTokenExpiringSoon, refreshNativeTokens } from '@/hooks/useNativeAuth';
import { debugLog } from '@/lib/debugLog';
import { beginNativeStartupTrace, markNativeStartup } from '@/lib/nativeStartupTrace';

const getIsNativePlatform = () => Capacitor.isNativePlatform();

const getKindeDomain = () => {
  const runtimeEnv = window.__ENV;
  return runtimeEnv?.VITE_KINDE_DOMAIN ?? import.meta.env.VITE_KINDE_DOMAIN ?? '';
};

const getNativeLogoutRedirectUrl = () => 'cleariest://auth/logout';

const getNativeLogoutUrl = () => {
  const domain = getKindeDomain();
  if (!domain) {
    return null;
  }

  const params = new URLSearchParams({
    redirect: getNativeLogoutRedirectUrl(),
    all_sessions: 'true',
  });

  return `${domain}/logout?${params.toString()}`;
};

interface User {
  userId: string;
  username: string;
  email: string;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  avatarUrl: string | null;
  isAdmin: boolean;
  showEmailToMembers: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const getAuthMeTimeoutMs = () => (getIsNativePlatform() ? 30000 : 12000);

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, timeoutMessage: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(new Error(timeoutMessage));
    }, timeoutMs);

    promise
      .then((value) => {
        window.clearTimeout(timeoutId);
        resolve(value);
      })
      .catch((error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      });
  });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const isNativePlatform = getIsNativePlatform();
  const {
    isAuthenticated: kindeAuthenticated,
    isLoading: kindeLoading,
    user: kindeUser,
    getAccessToken: kindeGetAccessToken,
    getToken,
    logout: kindeLogout,
  } = useKindeAuth();

  // Check for native auth tokens as fallback
  const nativeAuthenticated = isNativePlatform && isNativeTokenValid();

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    // On native platform, check for native tokens first
    if (isNativePlatform) {
      const nativeTokens = getNativeTokens();
      if (nativeTokens) {
        // If the access token is still fresh (>5 min remaining), use it directly
        if (!isNativeAccessTokenExpiringSoon()) {
          return nativeTokens.access_token;
        }

        // Access token is expired or expiring soon — try to refresh
        debugLog('[AuthContext] Access token expiring soon, attempting refresh...', {
          expiresAt: new Date(nativeTokens.expires_at).toISOString(),
          hasRefreshToken: !!nativeTokens.refresh_token,
        });
        if (nativeTokens.refresh_token) {
          const refreshed = await refreshNativeTokens();
          if (refreshed) {
            const freshTokens = getNativeTokens();
            debugLog('[AuthContext] Token refresh successful, new expiry:', freshTokens ? new Date(freshTokens.expires_at).toISOString() : 'n/a');
            if (freshTokens) {
              return freshTokens.access_token;
            }
          } else {
            debugLog('[AuthContext] Token refresh failed');
          }
        }

        // Refresh failed or no refresh token — return current token if not yet expired
        // (it may still be valid for a few more minutes within the buffer window)
        if (nativeTokens.expires_at > Date.now()) {
          debugLog('[AuthContext] Using existing token (not yet fully expired)');
          return nativeTokens.access_token;
        }

        // Token is fully expired and refresh failed — fall through to Kinde SDK
      }
    }
    
    // Fall back to Kinde SDK tokens (works on web, may not work on native)
    const token = (await kindeGetAccessToken?.()) ?? (await getToken?.());
    return token ?? null;
  }, [kindeGetAccessToken, getToken]);

  // Restore user from localStorage synchronously on mount to prevent ProtectedRoute redirects
  const [user, setUser] = useState<User | null>(() => {
    try {
      const storedUser = localStorage.getItem('user');
      return storedUser ? JSON.parse(storedUser) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const backgroundRefreshRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    beginNativeStartupTrace('auth_provider_mounted', {
      hasStoredUser: !!user,
    });
  }, []);

  // Set up token provider synchronously on mount to prevent 401s during page reload
  useLayoutEffect(() => {
    if (user) {
      setAccessTokenProvider(getAccessToken);
    }
  }, [user, getAccessToken]);

  // Restore user quickly from localStorage, then refresh from backend when possible.
  useEffect(() => {
    let cancelled = false;

    const clearAuthenticatedState = () => {
      if (cancelled) {
        return;
      }

      setUser(null);
      localStorage.removeItem('user');
      setLoading(false);
      setAccessTokenProvider(null);

      if (isNativePlatform) {
        clearNativeTokens();
      }
    };

    const refreshUserFromBackend = async (options: { background: boolean }) => {
      const mode = options.background ? 'background' : 'blocking';
      markNativeStartup('auth_me_refresh_started', { mode });

      try {
        let response = await withTimeout(
          authFetch(buildApiUrl('/api/auth/me')),
          getAuthMeTimeoutMs(),
          '[AuthContext] /api/auth/me timed out'
        );

        if (isNativePlatform && (response.status === 401 || response.status === 403)) {
          const refreshed = await refreshNativeTokens();
          if (refreshed) {
            response = await withTimeout(
              authFetch(buildApiUrl('/api/auth/me')),
              getAuthMeTimeoutMs(),
              '[AuthContext] /api/auth/me retry timed out'
            );
          }
        }

        if (cancelled) {
          return;
        }

        if (response.ok) {
          const data = (await response.json()) as User;
          setUser(data);
          localStorage.setItem('user', JSON.stringify(data));
          setAccessTokenProvider(getAccessToken);
          markNativeStartup('auth_me_refresh_succeeded', { mode });
          return;
        }

        if (isNativePlatform && (response.status === 401 || response.status === 403)) {
          markNativeStartup('auth_me_refresh_rejected', {
            mode,
            status: response.status,
          });

          setUser(null);
          localStorage.removeItem('user');
          localStorage.removeItem('currentWorkspaceId');
          localStorage.removeItem('workspaceId');
          setAccessTokenProvider(null);
          clearNativeTokens();
          return;
        }

        markNativeStartup('auth_me_refresh_failed', {
          mode,
          status: response.status,
        });
      } catch {
        console.warn('[AuthContext] Failed to refresh user from /api/auth/me, using stored data');
        markNativeStartup('auth_me_refresh_failed', { mode });
      } finally {
        if (!cancelled && !options.background) {
          setLoading(false);
        }
      }
    };

    const run = async () => {
      // On native, skip Kinde SDK loading check entirely.
      // Native auth is handled by the PKCE flow in useNativeAuth, not the Kinde SDK.
      // The Kinde SDK's isLoading may never resolve to false in a Capacitor WebView
      // because it can't complete its session check (no cookies/iframe support).
      const isAuthenticated = isNativePlatform
        ? nativeAuthenticated
        : (kindeAuthenticated || nativeAuthenticated);
      const isStillLoading = !isNativePlatform && kindeLoading;
      
      if (isStillLoading) {
        setLoading(true);
        markNativeStartup('auth_waiting_on_web_sdk');
        return;
      }

      if (!isAuthenticated) {
        markNativeStartup('auth_unauthenticated');
        clearAuthenticatedState();
        return;
      }

      setAccessTokenProvider(getAccessToken);

      // Skip /api/auth/me if no user - let KindeCallback/NativeAuth handle first-time sync
      // This prevents race condition where /api/auth/me is called before user is created
      if (!user) {
        console.log('[AuthContext] No stored user, skipping /api/auth/me (waiting for callback sync)');
        markNativeStartup('auth_waiting_for_callback_sync');
        setLoading(false);
        return;
      }

      if (isNativePlatform) {
        setLoading(false);
        markNativeStartup('auth_unblocked_from_cached_session', {
          userId: user.userId,
        });

        if (!backgroundRefreshRef.current) {
          backgroundRefreshRef.current = refreshUserFromBackend({ background: true }).finally(() => {
            backgroundRefreshRef.current = null;
          });
        }

        return;
      }

      await refreshUserFromBackend({ background: false });
    };

    void run();

    return () => {
      cancelled = true;
    };
  // Use user?.userId instead of user object to avoid re-triggering on every setUser
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kindeAuthenticated, kindeLoading, nativeAuthenticated, kindeUser?.id, getAccessToken, user?.userId]);

  const updateUser = useCallback((updates: Partial<User>) => {
    setUser(prev => {
      // If updates contain userId, it's a full user object - use it directly
      const hasUserId = 'userId' in updates && updates.userId;
      const updated = hasUserId ? (updates as User) : (prev ? { ...prev, ...updates } : null);
      
      if (updated) {
        localStorage.setItem('user', JSON.stringify(updated));
      }
      return updated;
    });
  }, []);

  const logout = useCallback(() => {
    const currentUserId = user?.userId;

    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('currentWorkspaceId');
    localStorage.removeItem('workspaceId');
    sessionStorage.removeItem('postAuthRedirect');

    // Clear native tokens on logout
    if (isNativePlatform) {
      clearNativeTokens();
      removeNativeAuthLoadingOverlay();
    }

    if (currentUserId) {
      authEvents.emit(AUTH_EVENTS.LOGOUT, { userId: currentUserId });
    }

    if (isNativePlatform) {
      void (async () => {
        try {
          const logoutUrl = getNativeLogoutUrl();

          if (!logoutUrl) {
            await kindeLogout?.({
              allSessions: true,
              redirectUrl: getNativeLogoutRedirectUrl(),
            });
            return;
          }

          const { Browser } = await import('@capacitor/browser');
          await Browser.open({
            url: logoutUrl,
            presentationStyle: 'popover',
            windowName: '_blank',
          });
        } catch (error) {
          console.error('[AuthContext] Native logout failed:', error);
          window.location.href = '/login';
        }
      })();

      return;
    }

    void kindeLogout?.();
  }, [isNativePlatform, kindeLogout, user?.userId]);

  const value = {
    user,
    loading,
    logout,
    updateUser,
    isAuthenticated: (isNativePlatform ? nativeAuthenticated : (kindeAuthenticated || nativeAuthenticated)) && !!user,
    isAdmin: user?.isAdmin || false,
    getAccessToken,
  };

  // Remove native auth loading overlay once the app has bootstrapped
  useEffect(() => {
    if (!loading) {
      removeNativeAuthLoadingOverlay();
    }
  }, [loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
